/** Interpolate `{name}` placeholders in a translated string.
 *
 * The app has no i18n library, so translated strings carry their own
 * placeholders and this replaces them in one pass. An unknown key is left as
 * written rather than blanked, which makes a typo visible instead of silent.
 *
 * `{name|one|many}` picks the wording that agrees with a count ("1 lead",
 * "3 leads"), so no string has to fall back on the "lead(s)" shorthand. Same
 * marker as the backend audit copy. Pass the reader's `locale` to follow its
 * plural rule (French counts 0 as singular); without it, only 1 is singular.
 *
 * Numbers are accepted because most placeholders are counts; format them for
 * the reader's locale before passing them in when they are money or volumes.
 */
export function fill(
  template: string,
  vars: Record<string, string | number>,
  locale?: string,
): string {
  const singular = (n: number) =>
    locale !== undefined ? isSingular(n, locale) : n === 1
  return template
    .replace(
      /\{(\w+)\|([^|{}]*)\|([^|{}]*)\}/g,
      (match, key: string, one: string, many: string) =>
        key in vars ? (singular(Number(vars[key])) ? one : many) : match,
    )
    .replace(/\{(\w+)\}/g, (match, key: string) =>
      key in vars ? String(vars[key]) : match,
    )
}

/** Whether a count takes the singular in `locale`: 0 and 1 in French, only 1 in English. */
export function isSingular(n: number, locale: string): boolean {
  return new Intl.PluralRules(locale).select(n) === "one"
}
